<template>
  <div>
    <ExpansionTable
      :list="list"
      :pagination="pagination"
      :ordering="filters.ordering"
      :loading="isLoading"
      :skeleton="isLoadingAsyncData"
      :readonly="readonly"

      :title="pageTitle"
      :creatable="creatable"

      sortable-positions
      show-expand
      single-expand

      @change:pagination="onChangePagination"
      @change:sortable-positions="onUpdatePositions"
      @change:sort="onChangeSort"

      @action:create="onActionCreate"
      @action:edit="onActionEdit"
      @action:view="onActionView"
      @action:delete="onActionDelete"
      @action:upload="onActionUpload"
    >
      <template #expanded-item="{ data, headers }">
        <td :key="data.id" :colspan="headers.length" class="pa-4">
          <PublicationStorageFilesList
            v-if="data.id"
            :storage="data"
            :publication="publication"
            @update="onGetData(filters)"
          />

          <v-divider class="mt-8 mb-12" />

          <PublicationStorageFilesHistoryList
            v-if="data.id"
            :storage="data"
            :publication="publication"
            @update="onGetData(filters)"
          />
        </td>
      </template>
    </ExpansionTable>

    <input
      v-show="false"
      ref="input"
      type="file"
      accept=".xlsx,.xlsm,.xltx,.xltm"
      @input="onUploadFilesTimes($event.target.files)"
    >
  </div>
</template>

<script>
import { lkPublicationStoragePermissions as Permissions } from '@/helpers/enums/permission-models'
import { createListMixin } from '@/helpers/mixins/createListMixin'
import { PublicationStorageModel as Model } from './Model'
import { publicationStorageStoreModule as StoreModule, publicationStorageMapper as StoreMapper } from './store'
import { createPublicationStorageModal } from './utils'

import PublicationStorageFilesList from './Files/PublicationStorageFilesList.vue'
import PublicationStorageFilesHistoryList from './FilesHistory/PublicationStorageFilesHistoryList.vue'
import ExpansionTable from './components/PublicationStorageExpansionTable.vue'


const mixinOptions = { Model, StoreModule }
const listMixin = createListMixin(mixinOptions)

/** @typedef { import('@/helpers/mixins/createListMixin').ListMixin<any, StoreModule> } List */

export default {
  name: 'ViewPublicationStorageList',
  components: {
    PublicationStorageFilesList,
    PublicationStorageFilesHistoryList,
    ExpansionTable,
  },
  mixins: [
    listMixin,
  ],
  props: {
    publication: {
      type: Object,
      required: true,
      validator: (prop) => (
        prop
        && 'id' in prop
      ),
    },
  },
  data() {
    return {
      disabledPaginationQuery: true,
      defaultParams: {
        publication_id: this.publication.id,
      },
    }
  },
  computed: {
    readonly() {
      return !this.$access(Permissions.edit)
    },
    creatable() {
      return this.$access(Permissions.create)
    },
  },
  /** @type { List } */
  methods: {
    ...StoreMapper.mapActions(['uploadFilesTimes']),
    onActionCreate() {
      const readonly = false
      this.onOpenModal(null, readonly)
    },
    onActionEdit(data) {
      const readonly = false
      this.onOpenModal(data, readonly)
    },
    onActionView(data) {
      const readonly = true
      this.onOpenModal(data, readonly)
    },
    async onActionDelete(data) {
      await this.onDeleteWithRefresh(data, this.defaultParams)
    },
    onActionUpload(data, callback) {
      this.unwatch = callback
      const { id } = data
      this.storageId = id
      this.$refs.input.click()
    },
    async onUploadFilesTimes(files) {
      this.unwatch?.()

      try {
        await this.uploadFilesTimes({ storage_id: this.storageId, file: files[0] })
      } catch (e) {
        this.$notice.errors(e)
      }

      this.unwatch?.()
      this.storageId = void 0
      this.unwatch = void 0
    },
    async onOpenModal(data, readonly) {
      const propsData = await this.getPropsData(data, readonly)
      const modal = await createPublicationStorageModal(this, propsData)
      await modal.open()
      this.onGetData(this.filters)
    },
    getPropsData(data, readonly = false) {
      const action = (payload) => {
        const options = {
          ...this.defaultParams,
          payload: { ...payload, ...this.defaultParams },
          id: payload.id,
        }

        const promise = payload.id
          ? this.update(options)
          : this.create(options)

        return promise
      }

      const propsData = {
        data,
        readonly,
        publication: this.publication,
        actionSave: action,
        actionDelete: this.onActionDelete,
        successText: data ? Model.response_success.update : Model.response_success.create,
        errorText: data ? Model.response_error.update : Model.response_error.create,
        ...this.defaultParams,
      }

      return propsData
    },
  },
}
</script>
